import { CONFIG, CONSUMER_TYPES, PRODUCER_TYPES, STORAGE_TYPES } from "./config";
import {
  type Axial,
  type Point,
  axialToPixel,
  hexCorners,
  hexesInRadius,
  parseHexKey,
  pixelToAxial,
} from "./hexgrid";
import type { SolveResult } from "./solver";
import type { GameState } from "./state";

export interface Camera {
  offsetX: number;
  offsetY: number;
  hexSize: number;
}

/** Screen-space margins reserved by UI chrome (the HUD up top, the
 * next-turn/save/load controls down below) that the map must stay clear
 * of. */
export interface SafeArea {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

const NO_SAFE_AREA: SafeArea = { top: 0, bottom: 0, left: 0, right: 0 };

// The full hex map's bounding box at hexSize = 1, in axial-pixel units —
// cached since CONFIG.mapRadius is fixed for the page's lifetime. Used to
// scale hexSize down (never up past CONFIG.hexSizePx) so the whole map
// always fits within whatever space isn't covered by UI chrome, regardless
// of window size or browser zoom — see makeCamera.
let cachedBoundsRadius = -1;
let cachedBounds: { minX: number; maxX: number; minY: number; maxY: number } | null = null;

function mapBoundsAtUnitSize(): { minX: number; maxX: number; minY: number; maxY: number } {
  if (cachedBounds && cachedBoundsRadius === CONFIG.mapRadius) return cachedBounds;
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const hex of hexesInRadius(CONFIG.mapRadius)) {
    const center = axialToPixel(hex, 1);
    for (const corner of hexCorners(center, 1)) {
      minX = Math.min(minX, corner.x);
      maxX = Math.max(maxX, corner.x);
      minY = Math.min(minY, corner.y);
      maxY = Math.max(maxY, corner.y);
    }
  }
  cachedBounds = { minX, maxX, minY, maxY };
  cachedBoundsRadius = CONFIG.mapRadius;
  return cachedBounds;
}

/** hexSize is fixed in CSS px (CONFIG.hexSizePx), so the map's on-screen
 * footprint doesn't shrink with browser zoom — only the viewport does. At
 * a normal window size that's harmless (the map fits with room to spare),
 * but as zoom eats into the visible viewport, a fixed-size map centered at
 * a fixed hex size eventually runs under the HUD or off the canvas edges
 * entirely, with no way to pan back to the hidden part. Fitting the whole
 * map into `safeArea` (shrinking hexSize as needed, never growing past the
 * configured default) keeps every hex reachable regardless of zoom or
 * window size, without needing a pan/scroll camera. */
export function makeCamera(canvasWidth: number, canvasHeight: number, safeArea: SafeArea = NO_SAFE_AREA): Camera {
  const safeWidth = Math.max(1, canvasWidth - safeArea.left - safeArea.right);
  const safeHeight = Math.max(1, canvasHeight - safeArea.top - safeArea.bottom);

  const bounds = mapBoundsAtUnitSize();
  const boundsWidth = bounds.maxX - bounds.minX;
  const boundsHeight = bounds.maxY - bounds.minY;

  const fitScale = Math.min(safeWidth / boundsWidth, safeHeight / boundsHeight) * 0.95;
  const hexSize = Math.max(10, Math.min(CONFIG.hexSizePx, fitScale));

  const boundsCenterX = (bounds.minX + bounds.maxX) / 2;
  const boundsCenterY = (bounds.minY + bounds.maxY) / 2;

  return {
    offsetX: safeArea.left + safeWidth / 2 - boundsCenterX * hexSize,
    offsetY: safeArea.top + safeHeight / 2 - boundsCenterY * hexSize,
    hexSize,
  };
}

export function hexToScreen(camera: Camera, a: Axial): Point {
  const p = axialToPixel(a, camera.hexSize);
  return { x: p.x + camera.offsetX, y: p.y + camera.offsetY };
}

export function screenToHex(camera: Camera, screenX: number, screenY: number): Axial {
  return pixelToAxial({ x: screenX - camera.offsetX, y: screenY - camera.offsetY }, camera.hexSize);
}

export interface Selection {
  pendingHex: Axial | null;
  hoverHex: Axial | null;
  inspectedNodeId: string | null;
  /** Mutually exclusive with inspectedNodeId — set by clicking a line in
   * Inspect mode, which shows its info (transported power, status)
   * instead of toggling its dismantle state the way Line mode does. */
  inspectedLineId: string | null;
}

const COLORS = {
  background: "#0b1220",
  hexStroke: "rgba(148, 163, 184, 0.25)",
  hexHover: "rgba(148, 163, 184, 0.15)",
  hexPending: "rgba(96, 165, 250, 0.35)",
  lineActive: "#94a3b8",
  lineDismantling: "#f59e0b",
  lineSaturated: "#f87171",
  control: "#e2e8f0",
  controlDismantling: "rgba(226, 232, 240, 0.4)",
  text: "#0b1220",
  inspectRing: "#facc15",
  producerOff: "#475569", // grayed-out fill when a producer's live output is 0%
};

export function render(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  preview: SolveResult,
  camera: Camera,
  selection: Selection,
): void {
  const { canvas } = ctx;
  ctx.fillStyle = COLORS.background;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  drawGrid(ctx, camera, selection);
  drawLines(ctx, state, preview, camera, selection);
  drawNodes(ctx, state, camera, selection);
}

function drawGrid(ctx: CanvasRenderingContext2D, camera: Camera, selection: Selection): void {
  const hexes = hexesInRadius(CONFIG.mapRadius);
  for (const hex of hexes) {
    const center = hexToScreen(camera, hex);
    const corners = hexCorners(center, camera.hexSize * 0.95);

    ctx.beginPath();
    corners.forEach((c, i) => (i === 0 ? ctx.moveTo(c.x, c.y) : ctx.lineTo(c.x, c.y)));
    ctx.closePath();

    if (selection.pendingHex && hex.q === selection.pendingHex.q && hex.r === selection.pendingHex.r) {
      ctx.fillStyle = COLORS.hexPending;
      ctx.fill();
    } else if (selection.hoverHex && hex.q === selection.hoverHex.q && hex.r === selection.hoverHex.r) {
      ctx.fillStyle = COLORS.hexHover;
      ctx.fill();
    }

    ctx.strokeStyle = COLORS.hexStroke;
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}

function drawLines(ctx: CanvasRenderingContext2D, state: GameState, preview: SolveResult, camera: Camera, selection: Selection): void {
  for (const line of Object.values(state.lines)) {
    const a = hexToScreen(camera, parseHexKey(line.a));
    const b = hexToScreen(camera, parseHexKey(line.b));
    const flow = preview.lineFlowsMw[line.id] ?? 0;
    const saturationRatio = Math.min(1, Math.abs(flow) / CONFIG.lineCapacityMw);
    const isSaturated = preview.saturatedLineIds.includes(line.id);

    if (selection.inspectedLineId === line.id) {
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.strokeStyle = COLORS.inspectRing;
      ctx.lineWidth = 1.5 + saturationRatio * 4 + 5;
      ctx.stroke();
    }

    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);

    if (isSaturated) {
      ctx.strokeStyle = COLORS.lineSaturated;
    } else if (line.status === "dismantling") {
      ctx.strokeStyle = COLORS.lineDismantling;
    } else {
      ctx.strokeStyle = COLORS.lineActive;
    }
    ctx.setLineDash(line.status === "dismantling" ? [6, 4] : []);
    ctx.lineWidth = 1.5 + saturationRatio * 4;
    ctx.stroke();
    ctx.setLineDash([]);

    if (line.status === "active" && Math.abs(flow) > 0.5) {
      drawFlowArrow(ctx, a, b, flow >= 0);
    }
  }
}

function drawFlowArrow(ctx: CanvasRenderingContext2D, a: Point, b: Point, aToB: boolean): void {
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  const from = aToB ? a : b;
  const to = aToB ? b : a;
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const size = 6;

  ctx.save();
  ctx.translate(mid.x, mid.y);
  ctx.rotate(angle);
  ctx.beginPath();
  ctx.moveTo(size, 0);
  ctx.lineTo(-size, -size * 0.6);
  ctx.lineTo(-size, size * 0.6);
  ctx.closePath();
  ctx.fillStyle = "#e8f0ff";
  ctx.globalAlpha = 0.8;
  ctx.fill();
  ctx.restore();
}

function drawNodes(ctx: CanvasRenderingContext2D, state: GameState, camera: Camera, selection: Selection): void {
  for (const node of Object.values(state.nodes)) {
    const center = hexToScreen(camera, { q: node.q, r: node.r });
    const radius = camera.hexSize * 0.45;
    const dimmed =
      (node.kind === "control" || (node.kind === "storage" && node.source === "battery")) &&
      node.status === "dismantling";

    ctx.globalAlpha = dimmed ? 0.5 : 1;

    if (node.kind === "producer") {
      const type = PRODUCER_TYPES[node.typeId];
      const isOff = node.effectivePct < 0.5;
      drawCircle(ctx, center, radius, isOff ? COLORS.producerOff : type.color);
      if (!isOff) drawOutputMeter(ctx, center, radius, node.effectivePct / 100);
      drawLabel(ctx, center, radius, type.label[0]);
    } else if (node.kind === "consumer") {
      const type = CONSUMER_TYPES[node.typeId];
      drawSquare(ctx, center, radius, type.color);
      drawLabel(ctx, center, radius, type.label[0]);
    } else if (node.kind === "storage") {
      const type = STORAGE_TYPES[node.source];
      drawHexShape(ctx, center, radius, type.color);
      const chargeFrac = node.chargeMwh / node.capacityMwh;
      drawChargeRing(ctx, center, radius, chargeFrac);
      drawLabel(ctx, center, radius, node.source === "dam" ? "D" : "B");
    } else if (node.kind === "control") {
      drawDiamond(ctx, center, radius, node.status === "dismantling" ? COLORS.controlDismantling : COLORS.control);
      drawLabel(ctx, center, radius, "C");
    }

    if (selection.inspectedNodeId === node.id) {
      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.arc(center.x, center.y, radius + 10, 0, Math.PI * 2);
      ctx.strokeStyle = COLORS.inspectRing;
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    ctx.globalAlpha = 1;
  }
}

function drawCircle(ctx: CanvasRenderingContext2D, center: Point, radius: number, color: string): void {
  ctx.beginPath();
  ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.strokeStyle = COLORS.background;
  ctx.lineWidth = 2;
  ctx.stroke();
}

function drawSquare(ctx: CanvasRenderingContext2D, center: Point, radius: number, color: string): void {
  const s = radius * 1.5;
  ctx.beginPath();
  ctx.rect(center.x - s / 2, center.y - s / 2, s, s);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.strokeStyle = COLORS.background;
  ctx.lineWidth = 2;
  ctx.stroke();
}

function drawDiamond(ctx: CanvasRenderingContext2D, center: Point, radius: number, color: string): void {
  const s = radius * 1.1;
  ctx.beginPath();
  ctx.moveTo(center.x, center.y - s);
  ctx.lineTo(center.x + s, center.y);
  ctx.lineTo(center.x, center.y + s);
  ctx.lineTo(center.x - s, center.y);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  ctx.strokeStyle = COLORS.background;
  ctx.lineWidth = 2;
  ctx.stroke();
}

function drawHexShape(ctx: CanvasRenderingContext2D, center: Point, radius: number, color: string): void {
  const corners = hexCorners(center, radius * 1.05);
  ctx.beginPath();
  corners.forEach((c, i) => (i === 0 ? ctx.moveTo(c.x, c.y) : ctx.lineTo(c.x, c.y)));
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  ctx.strokeStyle = COLORS.background;
  ctx.lineWidth = 2;
  ctx.stroke();
}

function drawChargeRing(ctx: CanvasRenderingContext2D, center: Point, radius: number, frac: number): void {
  ctx.beginPath();
  ctx.arc(center.x, center.y, radius + 4, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * Math.max(0, Math.min(1, frac)));
  ctx.strokeStyle = "#e8f0ff";
  ctx.lineWidth = 2;
  ctx.stroke();
}

/** A producer's live-output meter: a translucent "fill level" rising from
 * the bottom of its circle, clipped to the circle's own shape — distinct
 * from storage's charge ring (drawn *outside* the shape) on purpose, so
 * the two aren't visually confusable at a glance despite both being
 * percent-fill indicators. */
function drawOutputMeter(ctx: CanvasRenderingContext2D, center: Point, radius: number, frac: number): void {
  const clamped = Math.max(0, Math.min(1, frac));
  if (clamped <= 0) return;
  ctx.save();
  ctx.beginPath();
  ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
  ctx.clip();
  const fillHeight = radius * 2 * clamped;
  ctx.fillStyle = "rgba(255, 255, 255, 0.35)";
  ctx.fillRect(center.x - radius, center.y + radius - fillHeight, radius * 2, fillHeight);
  ctx.restore();
}

function drawLabel(ctx: CanvasRenderingContext2D, center: Point, radius: number, text: string): void {
  ctx.fillStyle = COLORS.text;
  ctx.font = `bold ${Math.round(radius * 0.9)}px sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, center.x, center.y);
}
